import {
  buildCompressedVehicleImageKey,
  getVehicleImageStorageKey,
  type VehicleImage,
} from "@repo/types";

const CACHE_TTL_MS = 4 * 60 * 1000;
const MAX_CACHE_ENTRIES = 500;
const COMPRESSED_MEDIA_BASE_URL = "https://alb-compressed-media.s3.eu-west-3.amazonaws.com";
const preloadedImageCache = new Map<string, { expiresAt: number; resolution: Promise<string> }>();

function prunePreloadedImageCache(now: number) {
  for (const [key, entry] of preloadedImageCache) {
    if (entry.expiresAt <= now) preloadedImageCache.delete(key);
  }

  while (preloadedImageCache.size >= MAX_CACHE_ENTRIES) {
    const oldestKey = preloadedImageCache.keys().next().value;
    if (!oldestKey) break;
    preloadedImageCache.delete(oldestKey);
  }
}

function preloadImage(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve();
    image.onerror = () => reject(new Error("Failed to preload image"));
    image.src = url;
  });
}

/**
 * Checks and preloads a public compressed vehicle image when one is available.
 * The original is returned only when the compressed object is unavailable.
 */
export function preloadCompressedVehicleImage(
  image: Pick<VehicleImage, "key" | "url">
): Promise<string> {
  const sourceKey = getVehicleImageStorageKey(image);
  const compressedKey = sourceKey ? buildCompressedVehicleImageKey(sourceKey) : null;
  if (!sourceKey || !compressedKey) {
    return Promise.resolve(image.url);
  }

  const now = Date.now();
  const cached = preloadedImageCache.get(sourceKey);
  if (cached?.expiresAt && cached.expiresAt > now) return cached.resolution;
  if (cached) preloadedImageCache.delete(sourceKey);

  const resolution = (async () => {
    try {
      const compressedUrl = `${COMPRESSED_MEDIA_BASE_URL}/${compressedKey}`;
      const response = await fetch(compressedUrl, { method: "HEAD" });
      if (!response.ok) return image.url;

      await preloadImage(compressedUrl);
      return compressedUrl;
    } catch {
      return image.url;
    }
  })();

  const cacheEntry = {
    expiresAt: now + CACHE_TTL_MS,
    resolution,
  };
  prunePreloadedImageCache(now);
  preloadedImageCache.set(sourceKey, cacheEntry);
  return resolution;
}

export function preloadCompressedVehicleImages(
  images: Iterable<Pick<VehicleImage, "key" | "url">>
): Promise<void> {
  return Promise.all([...images].map(preloadCompressedVehicleImage)).then(() => undefined);
}
